import { useEffect, useState } from 'react';

import clientApi from '../utils/api/client';
import schedulerApi from '../utils/api/inspectionSchedular';
import projectApi from '../utils/api/project';

/**
 * SW2-2136 — whether closing a finding on this project's form requires a closure photo. The same
 * rule the server applies: the client setting wins, then the form's flag, then the project's.
 *
 * Answers `null` while unknown — still loading, no project, or the project or client lookup failed.
 * If only the form list fails (it has no offline cache), the project's flag answers. Callers
 * choose which way unknown falls:
 *  - Add Finding hides Date Completed unless the answer is `false`. Hiding it costs nothing — the
 *    finding is closed afterwards — while showing it wrongly gets the create refused by the
 *    server, and offline that is a queued create that can never sync.
 *  - Close screens treat unknown as not required, as they always have; the server decides.
 *
 * Loads the project itself from `projectId`: the map editor's layout does not put the project in
 * ProjectContext, so a caller reading it from there would have nothing to pass.
 */
/** The lookup behind useRequireClosurePhotos: `true`, `false`, or `null` when unknown. */
export async function lookupRequireClosurePhotos(
  projectId: number | null | undefined,
  templateId: number | null | undefined,
): Promise<boolean | null> {
  if (!projectId) return null;

  try {
    const project = await projectApi.get(projectId);

    if (!project?.client_id) return null;

    const client = await clientApi.getCustomFields(project.client_id, [
      'require_closure_photos',
    ]);

    if (!client?.require_closure_photos) return false;
    if (!templateId) return Boolean(project.require_closure_photos);

    // The form list has no offline cache. When only it fails, the project's flag decides — the
    // fallback the map editor always had — rather than "unknown", which would let a close screen
    // queue a close with no closure photo that the server then refuses.
    const forms = await schedulerApi.getProjectForms(projectId).catch(() => null);
    const form = (forms?.data || []).find((f: any) => f.template_id === templateId);

    return form
      ? Boolean(form.require_closure_photos)
      : Boolean(project.require_closure_photos);
  } catch {
    return null;
  }
}

export function useRequireClosurePhotos(
  projectId: number | null | undefined,
  templateId: number | null | undefined,
): boolean | null {
  const [required, setRequired] = useState<boolean | null>(null);

  useEffect(() => {
    let cancelled = false;

    setRequired(null);
    lookupRequireClosurePhotos(projectId, templateId).then((value) => {
      if (!cancelled) setRequired(value);
    });

    return () => {
      cancelled = true;
    };
  }, [projectId, templateId]);

  return required;
}
